import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import moment from 'moment-timezone';

import { tensesEnum } from '../enums/tenses-enum';
import { DateQueryModel } from '../models/date-query.model';
import { DateRangeModel } from '../models/date-range.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';


export type DayKey = string; // 'YYYY-MM-DD'

@Injectable()
export class DateHelper {

    // Our standard display format
    static toDateString(date: Date, timezone?: string, format?: string): string {
        let dateFormat;
        if (format) {
            dateFormat = format;
        } else {
            dateFormat = 'MM/DD/YYYY';
        }
        if (timezone) {
            return moment(date).tz(timezone).format(dateFormat);
        } else {
            return moment(date).format(dateFormat);
        }
    }


    /**
     * Converts JS Date 'instant' into DayKey
     * @returns DayKey `YYYY-MM-DD`
     */
    static toDayKey(date: Date): DayKey {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    /**
     * Converts DayKey into JS Date
    */
    static fromDayKey(key: DayKey): Date {
        const [y, m, d] = key.split('-').map(Number);
        return new Date(y, m - 1, d);
    }

    /**
    * Returns a date offset from the current moment based on provided
    * hours, minutes, and tense (past or future) which updates continually
    */
    getOffsetDate(hours: number, minutes: number, tense: tensesEnum): Observable<Date> {
        const oneSecond: number = 1000;
        const oneMinute: number = 60 * oneSecond;
        const oneHour: number = 60 * oneMinute;
        let interval: NodeJS.Timeout;

        const offsetDate = Observable.create((observer) => {
            interval = setInterval(() => {
                observer.next(
                    this.createOffsetDate(hours, minutes, tense)
                );
            }, oneMinute);

            observer.next(
                this.createOffsetDate(hours, minutes, tense)
            );
        });

        offsetDate.pipe(finalize(() => {
            clearInterval(interval);
        }));

        return offsetDate;
    }


    /**
    * Create a date offset from the current moment based on provided
    * hours, minutes, and tense (past or future)
    */
    private createOffsetDate(
        hours: number,
        minutes: number,
        tense: tensesEnum
    ): Date {
        const now: Date = new Date();
        const dateModifier: moment.Moment = moment(now);

        if (tense === tensesEnum.past) {
            dateModifier.subtract(minutes, 'minutes');
            dateModifier.subtract(hours, 'hours');
        } else if (tense === tensesEnum.future) {
            dateModifier.add(minutes, 'minutes');
            dateModifier.add(hours, 'hours');
        }

        const newDate: Date = dateModifier.toDate();

        return newDate;
    }

    /**
    * Takes a date query and generates a static date range
    */
    getDateRangeFromQuery(dateQuery: DateQueryModel): DateRangeModel {
        let dateRange: DateRangeModel = null;
        const hasDateQuery: boolean = dateQuery !== null && dateQuery !== undefined;
        const isAbsoluteRange: boolean = hasDateQuery && dateQuery.type === DateQueryTypeEnum.ABSOLUTE;
        const isRelativeDate: boolean = hasDateQuery && dateQuery.type === DateQueryTypeEnum.RELATIVE;
        const momentPresent: moment.Moment = moment().utc();
        let startDate: Date = null;
        let endDate: Date = null;

        if (isAbsoluteRange) {
            startDate = dateQuery.absoluteRange.start;
            endDate = dateQuery.absoluteRange.end;
        } else if (isRelativeDate) {
            const momentStartDate: moment.Moment = momentPresent.clone();
            const momentEndDate: moment.Moment = momentPresent.clone();

            momentStartDate.subtract({ 'hours': dateQuery.relativeStartOffsetH });
            momentEndDate.subtract({ 'hours': dateQuery.relativeEndOffsetH });

            startDate = momentStartDate.toDate();
            endDate = momentEndDate.toDate();
        }

        dateRange = {
            start: startDate,
            end: endDate
        };
        return dateRange;
    }
}
